"use client";

import React, { useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Card,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import Image from "next/image";
import geetaPhoto from "../../assets/geeta-ma'am-final.png";
import vishalPhoto from "../../assets/vishal-sir-final.png";
import {
  Facebook,
  Twitter,
  Share,
  LinkedIn,
  Instagram,
} from "@mui/icons-material";
import "./founder.css";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 1 } },
};

const FounderTeam = () => {
  const [hoverGeeta, setHoverGeeta] = useState(false);
  const [hoverVishal, setHoverVishal] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const heroGradient =
    "linear-gradient(135deg, #123524 0%, #1f7a4d 55%, #1a6b42 100%)";
  const namePlateGradient =
    "linear-gradient(135deg, #1f7a4d 0%, #123524 100%)";

  return (
    <Box
      sx={{
        color: "#123524",
        pb: { xs: 2, sm: 3 },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          background: heroGradient,
          py: { xs: 5, sm: 7 },
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 12px 40px rgba(18, 53, 36, 0.2)",
        }}
      >
        <motion.div
          animate={{ opacity: [0, 1], x: [-50, 0] }}
          transition={{ duration: 1.5 }}
          style={{
            position: "absolute",
            right: isSmallScreen ? "0%" : "5%",
            top: isSmallScreen ? "0%" : "20%",
            fontSize: isSmallScreen ? "2.5rem" : "5rem",
            color: "rgba(255, 255, 255, 0.12)",
          }}
        >
          &#128206;
        </motion.div>

        <motion.div initial="hidden" animate="visible" variants={fadeIn}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: "white",
              fontSize: { xs: "26px", sm: "44px" },
              textShadow: "0 2px 24px rgba(0, 0, 0, 0.15)",
              px: 2,
            }}
          >
            A message from our founders
          </Typography>
        </motion.div>
      </Box>

      {/* Main Content */}
      <Box>
        <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 } }}>
          <Grid container spacing={4} alignItems="center">
            {/* Geeta Chetri — CEO & Co-founder */}
            <Grid item xs={12} md={4}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.5 }}
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                style={{
                  position: "relative",
                }}
              >
                <Card
                  sx={{
                    background: "#fff",
                    color: "#123524",
                    textAlign: "center",
                    borderRadius: 3,
                    overflow: "hidden",
                    border: "1px solid rgba(18, 53, 36, 0.1)",
                    boxShadow: "0 12px 40px rgba(18, 53, 36, 0.1)",
                  }}
                >
                  <Image
                    src={geetaPhoto}
                    width={500}
                    height={500}
                    alt="Geeta Chetri"
                    style={{ width: "100%", height: "auto", display: "block" }}
                  />
                  <Box
                    className="user_social"
                    onMouseEnter={() => setHoverGeeta(true)}
                    onMouseLeave={() => setHoverGeeta(false)}
                  >
                    <Box
                      className={`social-icons ${hoverGeeta ? "show" : "hide"}`}
                    >
                      <IconButton
                        component="a"
                        href="https://www.facebook.com/salontym/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Facebook />
                      </IconButton>
                      <IconButton
                        component="a"
                        href="https://www.linkedin.com/in/itsgeetachetri/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <LinkedIn />
                      </IconButton>
                      <IconButton
                        component="a"
                        href="https://www.instagram.com/salon_tym/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Instagram />
                      </IconButton>
                      <IconButton
                        component="a"
                        href="https://x.com/salontym"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Twitter />
                      </IconButton>
                    </Box>

                    <Box className="s2-share_btn">
                      <IconButton>
                        <Share />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      background: namePlateGradient,
                      position: isSmallScreen ? "relative" : "absolute",
                      bottom: isSmallScreen ? "0px" : "2rem",
                      right: isSmallScreen ? "0px" : "-12rem",
                      width: isSmallScreen ? "-webkit-fill-available" : "15rem",
                      color: "#fff",
                      padding: "12px 14px",
                      borderRadius: isSmallScreen ? 0 : 2,
                    }}
                  >
                    <Typography variant="h6" fontWeight={700}>
                      Geeta Chetri
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.95 }}>
                      CEO & Co-founder
                    </Typography>
                  </Box>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={8}>
              <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#000",
                    fontSize: "1.1rem",
                    lineHeight: 1.8,
                    textAlign: "start",
                  }}
                >
                  &#34;At SalonTym, our goal is to empower beauty professionals
                  while offering customers a seamless and premium salon
                  experience. We believe in quality, customer satisfaction, and
                  accessibility to the best beauty services at any time. We are
                  grateful to our users and partners who trust us to bring
                  convenience and luxury to their doorstep.&#34;
                </Typography>
              </motion.div>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={4}
            alignItems="center"
            sx={{ display: "flex", flexDirection: "row-reverse" }}
          >
            {/* Vishal Saini — CTO & Co-founder */}
            <Grid item xs={12} md={4}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.5 }}
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                style={{
                  position: "relative",
                }}
              >
                <Card
                  sx={{
                    background: "#fff",
                    color: "#123524",
                    textAlign: "center",
                    borderRadius: 3,
                    overflow: "hidden",
                    border: "1px solid rgba(18, 53, 36, 0.1)",
                    boxShadow: "0 12px 40px rgba(18, 53, 36, 0.1)",
                  }}
                >
                  <Image
                    src={vishalPhoto}
                    width={500}
                    height={500}
                    alt="Vishal Saini"
                    style={{ width: "100%", height: "auto", display: "block" }}
                  />
                  <Box
                    className="user_social"
                    onMouseEnter={() => setHoverVishal(true)}
                    onMouseLeave={() => setHoverVishal(false)}
                  >
                    <Box
                      className={`social-icons ${hoverVishal ? "show" : "hide"}`}
                    >
                      <IconButton
                        component="a"
                        href="https://www.facebook.com/salontym/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Facebook />
                      </IconButton>
                      <IconButton
                        component="a"
                        href="https://www.linkedin.com/in/vishal-s-938a2296/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <LinkedIn />
                      </IconButton>
                      <IconButton
                        component="a"
                        href="https://www.instagram.com/salon_tym/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Instagram />
                      </IconButton>
                      <IconButton
                        component="a"
                        href="https://x.com/salontym"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Twitter />
                      </IconButton>
                    </Box>
                    <Box className="s2-share_btn">
                      <IconButton>
                        <Share />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      background: namePlateGradient,
                      position: isSmallScreen ? "relative" : "absolute",
                      bottom: isSmallScreen ? "0px" : "2rem",
                      left: isSmallScreen ? "0px" : "-12rem",
                      width: isSmallScreen ? "-webkit-fill-available" : "15rem",
                      color: "#fff",
                      padding: "12px 14px",
                      borderRadius: isSmallScreen ? 0 : 2,
                    }}
                  >
                    <Typography variant="h6" fontWeight={700}>
                      Vishal Saini
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.95 }}>
                      CTO & Co-founder
                    </Typography>
                  </Box>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={8}>
              <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#000",
                    fontSize: "1.1rem",
                    lineHeight: 1.8,
                    textAlign: "start",
                  }}
                >
                  &#34;At SalonTym, we believe in transforming beauty services
                  with seamless booking experiences and top-tier professionals
                  at your fingertips. Our journey is driven by innovation,
                  passion, and the commitment to redefine the salon experience.
                  We thank our customers and partners for trusting us.&#34;
                </Typography>
              </motion.div>
            </Grid>
          </Grid>
          <Grid container spacing={4} alignItems="left">
            <Grid item xs={12}>
              <motion.div initial="hidden" animate="visible" variants={fadeIn}>
                <br />
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    display: "inline-block",
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    border: "1px solid rgba(18, 53, 36, 0.12)",
                    background:
                      "linear-gradient(135deg, rgba(223, 246, 232, 0.95) 0%, rgba(246, 251, 248, 0.98) 100%)",
                    color: "#123524",
                  }}
                >
                  Our journey & core values
                </Typography>
                <br />
                <Typography
                  variant="body1"
                  sx={{
                    color: "#000",
                    fontSize: "1.1rem",
                    lineHeight: 1.8,
                    textAlign: "start",
                  }}
                >
                  Every great vision starts small, and ours began in a single
                  room with just a simple idea—to redefine the salon industry
                  with innovation, convenience, and trust. <br />
                  <br />
                  From our first customer booking to now serving hundreds, our
                  journey has been filled with passion, challenges, and
                  milestones. Today, we stand in a thriving office, supported by
                  a dedicated team and a growing community that believes in our
                  mission. We are not just a platform; we are a movement
                  dedicated to transforming beauty and wellness services. <br />
                  <br />✅ <strong>Seamless Booking Experience</strong> – What
                  started as a manual process is now a smart, technology-driven
                  solution that ensures effortless salon appointments. <br />✅{" "}
                  <strong>Top-Tier Professionals</strong> – We handpick and
                  onboard the best beauty experts, making premium services
                  accessible to all. <br />✅ <strong>Innovation-Driven</strong>{" "}
                  – Our commitment to continuous improvement keeps us ahead in
                  providing the best salon experience. <br />✅{" "}
                  <strong>Customer-Centric Approach</strong> – Every decision we
                  make revolves around enhancing convenience, quality, and
                  satisfaction. <br />✅{" "}
                  <strong>Passion for Beauty & Wellness</strong> – We believe in
                  self-care and are dedicated to making it easier and more
                  enjoyable for everyone. <br />✅{" "}
                  <strong>Building Trust & Relationships</strong> – From one
                  room to a big office, we have grown with the trust of our
                  customers and partners. Your support drives us forward. <br />
                  <br />
                  This is just the beginning. With every step forward, we remain
                  committed to making beauty and wellness services more
                  seamless, reliable, and extraordinary. Thank you for being
                  part of our journey! 🚀
                </Typography>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default FounderTeam;
